import React, { useState } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import './TrackingCard.css';
import Api from '@api';
/**
 * One trip item within the tracking list.
 * Shows vehicle, driver, speed, gender counts and passenger pills.
 */
function TrackingCard({ trip, selectedTripId, onSelectTrip, disableHover = false, urlTokens = {}, isOnlineVehicle = false}) {
  const meta = trip.metadata || {};
  const driver = isOnlineVehicle ? (trip.driverInfo || meta.driverInfo || {}) : (meta.driverInfo || {});
  const veh = isOnlineVehicle ? (trip.vehicleInfo || meta.vehicleInfo || {}) : (meta.vehicleInfo || {});
  const info = meta.tripInfo || {};
  const onBoard = meta.passengersInfo?.onBoardPassengers || [];
  const speedMs = Number(trip.speed || 0);
  const speedKmh = Math.round((speedMs * 3.6 + Number.EPSILON) * 10) / 10;
  
  // State for copy feedback
  const [copied, setCopied] = useState(false);
  
  // Calculate female passenger count
  const femaleCount = onBoard.filter(p => 
    (p?.gender || '').toLowerCase().startsWith('f')
  ).length;

  // Calculate male passenger count
  let maleCount = onBoard.filter(p => 
    (p?.gender || '').toLowerCase().startsWith('m')
  ).length;

  // Calculate ETA time and format it for display (exact time, no rounding)
  const etaMinutes = trip.finalDestinationETA / 60;
  let etaTime, etaUnit;
  
  if (etaMinutes >= 60) {
    const hours = Math.floor(etaMinutes / 60);
    const minutes = Math.floor(etaMinutes % 60);
    etaTime = `${hours}:${minutes.toString().padStart(2, '0')}`;
    etaUnit = hours > 1 ? 'hours' : 'hour';
  } else {
    etaTime = Math.floor(etaMinutes);
    etaUnit = Math.floor(etaMinutes) === 1 ? 'minute' : 'minutes';
  }
  const isLoginTrip = info.tripType === 'UPTRIP';
  const rawStatus = String(trip.tripStatus || info.tripStatus || '').toUpperCase();
  const isStarted = rawStatus === 'STARTED';
  const isCompleted = rawStatus === 'COMPLETED';
  const hasGPSLost = trip?.GPS_SIGNAL_LOST === true;

  const shareURL = `https://${window.location.host}/public/live-tracking/${urlTokens[trip.tripId]}`;

  const handleCopy = async (tid) => {
    try {
      await navigator.clipboard.writeText(shareURL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  const handleWhatsAppShare = () => {
    const message = `Track your live trip ${meta?.tripInfo?.tripCode} from here: ${shareURL}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleGmailShare = () => {
    const subject = 'Your Live Trip Tracking Link';
    const message = `Hi,\n\nYou can track your live trip - ${meta?.tripInfo?.tripCode} in real time using the link below:\n\n${shareURL}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
    window.open(emailUrl, '_blank');
  };

  return (
    <div key={trip.tripId || trip.driverId} className={`tracking-card ${selectedTripId === trip.tripId ? 'selected' : ''} ${disableHover ? 'no-hover' : ''} ${isOnlineVehicle ? 'online-vehicle' : ''} ${hasGPSLost ? 'gps-lost' : ''}`} onClick={() => !isOnlineVehicle && onSelectTrip(trip.tripId)}>
      <div className="tracking-row">
        <div className="track-veh">{isOnlineVehicle ? (veh.vehicleNo || driver.driverName || 'Online Vehicle') : (info.tripCode || '-')}</div>
        {isOnlineVehicle && <div className="track-pill online">Online</div>}
        {hasGPSLost && (
          <div className="track-pill gps-lost-pill">
            <GpsOffIcon fontSize="small" style={{fontSize: '14px', marginRight: '2px'}} />
            GPS Lost
          </div>
        )}
      </div>
      {!isOnlineVehicle && (
        <div className="tracking-row">
          {femaleCount > 0 && <div className="track-pill female">Female</div>}
          {info.escortTrip === 'YES' && <div className="track-pill escort">Escort</div>}
          <div className={`track-pill ${isLoginTrip ? 'login' : 'logout'}`}>{isLoginTrip ? 'Login' : 'Logout'}</div>
          <div className={`track-pill ${info.tripCategory === "ADHOCTRIP" ? "adhoc" : "regular"}`}>
          {info.tripCategory === "ADHOCTRIP" ? "Adhoc" : "Regular"}
          </div>
        </div>
      )}
      <div className="tracking-row-container">
        {/* <div className={`track-speed-card ${isCompleted ? 'completed' : ''}`}>
           <div className='track-speed-icon'> 
            <AccessTimeIcon fontSize='small'/> 
            <span>ETA</span> 
           </div>
           <div className='track-speed-value'>
            {isCompleted ? 
              <div className="track-status completed">Trip Completed</div> :
              <>
                <span className="track-speed-bold">{etaTime}</span> 
                <span className='track-speed-unit'>{etaUnit}</span>
              </>
          }
           </div>
        </div> */}
        <div className={`track-speed-card ${trip.isOverSpeeding && 'overspeed'}`}>
            <div className='track-speed-icon'> <SpeedIcon fontSize='small'/> <span>Current Speed</span> </div>
            <div className='track-speed-value'>
              <span className="track-speed-bold">{isCompleted ? '0' : speedKmh}</span> 
              <span className='track-speed-unit'>km/h</span>
              {trip.isOverSpeeding && (
              <WarningAmberIcon fontSize='small' className='overspeed-icon' />
            )}
            </div>
            
        </div>
      </div>


      <div className='track-speed-container'>
          <div className='track-speed-icon'> <PersonIcon fontSize='small'/> 
            <span>Driver</span> 
          </div>
          <div>
            {driver.driverName || '-'}
            {driver.driverMobileNo && 
            <>
              <span className="vehicle-type">({driver.driverMobileNo})</span>
            </>
            }
        </div>
      </div>
      <div className='track-speed-container'>
          <div className='track-speed-icon'> <DriveEtaIcon fontSize='small'/> <span>Vehicle</span> </div>
          <div>
            {veh.vehicleNo || '-'} 
            {veh.vehicleType && 
            <>
              <span className="vehicle-type">({veh.vehicleType})</span>
            </>
            }
        </div>
      </div>
      {!isOnlineVehicle && info.tripTime && (
        <div className='track-speed-container'>
            <div className='track-speed-icon'> <AccessTimeIcon fontSize='small'/> <span>Trip Time</span> </div>
            <div>
              {info.tripTime}
            </div>
        </div>
      )}
      {!isOnlineVehicle && info.escortTrip === 'YES' && (
        <div className='track-speed-container'>
            <div className='track-speed-icon'> <SecurityIcon fontSize='small'/> <span>Escort</span> </div>
            <div>
              {meta.escortInfo?.escortName || '-'}
            </div>
        </div>
      )}

      {!isOnlineVehicle && (
        <>
          <div className='track-speed-container'>
            <div className='track-speed-icon'> 
              <PeopleAltIcon fontSize='small'/> 
              <span>Passengers ({onBoard.length})</span> 
            </div>
            <div className="passenger-counts">
              <div className="passenger-count-item">
                <span className="count-label">M:</span>
                <span className="count-value">{maleCount}</span>
              </div>
              <div className="passenger-count-item">
                <span className="count-label">F:</span>
                <span className="count-value">{femaleCount}</span>
              </div>
            </div>
          </div>
          <div className='passenger-container'>
            {onBoard.map((p, idx) => {
              const isFemale = (p?.gender || '').toLowerCase().startsWith('f');
              return (
                <div key={idx} className='passenger'>
                  <div className={`passenger-avatar ${isFemale ? 'female-avatar' : ''}`}>
                   <span>
                    {p.name.charAt(0).toUpperCase()}
                   </span>
                  </div>
                  <span className='passenger-name'>{p.name}</span>
                </div>
              );
            })}
          </div>

          <div className='sharing-container'>
            <WhatsAppIcon
              className='sharing-icon whatsapp-icon'
              onClick={(e) => {
                e.stopPropagation();
                handleWhatsAppShare(trip.tripId);
              }}
              sx={{fontSize: '20px', mr: 2, color: '#25D366', cursor: 'pointer'}}
            />
            <EmailIcon
              className='sharing-icon email-icon'
              onClick={(e) => {
                e.stopPropagation();
                handleGmailShare(trip.tripId);
              }}
              sx={{fontSize: '20px', mr: 2, color: '#BB001B', cursor: 'pointer'}}
            />
            {copied ? (
              <CheckCircleIcon
                className='sharing-icon copy-icon'
                style={{color: 'green', fontSize: '16px'}}
              />
            ) : (
              <ContentCopyIcon
                className='sharing-icon copy-icon'
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(trip.tripId);
                }}
                style={{fontSize: '16px', cursor: 'pointer'}}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
export default TrackingCard;